/**
 * Notification Settings Page — Tabbed: Announcements | Settings
 * Announcements tab: chronological thread of all announcements (full body)
 * Settings tab: user preferences for push notifications
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import useNotifications from '../hooks/useNotifications';
import {
  Bell,
  BellOff,
  Megaphone,
  MessageCircle,
  BookOpen,
  Trophy,
  TrendingUp,
  Award,
  MessageSquare,
  CreditCard,
  Users,
  Clock,
  Mail,
  Smartphone,
  Save,
  AlertCircle,
  Info,
  Moon,
  Globe,
  ExternalLink,
  Settings,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  isNotificationSupported,
  getNotificationPermission,
  requestNotificationPermission,
  getFCMTokenAndSave
} from './notificationPermission';

// ─── Icon maps (shared with NotificationBell) ───
const ICON_MAP = { BookOpen, Award, TrendingUp, MessageSquare, Megaphone, Info };
const TYPE_ICON_MAP = {
  course_available: 'BookOpen',
  certificate_ready: 'Award',
  milestone: 'TrendingUp',
  chat_message: 'MessageSquare',
  system: 'Megaphone',
};

function formatDate(date) {
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
  return `${dateStr} at ${time}`;
}

// ─── Default notification preferences by role ───
const DEFAULT_PREFERENCES = {
  learner: {
    newCourses: true,
    courseMilestones: true,
    dailyReminders: false,
    chatMessages: true,
    newTools: false,
    subscriptionRenewal: true,
    certificateReady: true,
    weeklyProgress: false
  },
  admin: {
    newSignups: true,
    chatMessages: true,
    agentClockInOut: true,
    payments: true,
    supportTickets: true
  },
  specialist: {
    assignedChats: true,
    highPriorityMessages: true
  },
  mentor: {
    menteeMessages: true,
    sessionReminders: true
  }
};

// ═══════════════════════════════════════════════════════════════
// ANNOUNCEMENTS TAB
// ═══════════════════════════════════════════════════════════════
function AnnouncementsTab({ isDark }) {
  const { notifications, loading, markRead } = useNotifications();
  const announcements = notifications.filter((n) => n.source === 'announcement');

  const handleCardClick = (notif) => {
    markRead(notif);
  };

  if (loading) {
    return (
      <div className="space-y-4 mt-6">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className={`rounded-xl p-5 border animate-pulse ${
              isDark ? 'bg-white/5 border-gray-800' : 'bg-gray-50 border-gray-200'
            }`}
          >
            <div className="flex gap-3">
              <div className={`w-10 h-10 rounded-lg ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} />
              <div className="flex-1 space-y-2">
                <div className={`h-4 rounded w-2/3 ${isDark ? 'bg-white/10' : 'bg-gray-200'}`} />
                <div className={`h-3 rounded w-full ${isDark ? 'bg-white/5' : 'bg-gray-100'}`} />
                <div className={`h-3 rounded w-1/2 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (announcements.length === 0) {
    return (
      <div className={`text-center py-20 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        <Bell size={48} className="mx-auto mb-4 opacity-30" />
        <p className="text-lg font-medium">No announcements yet</p>
        <p className="text-sm mt-1 opacity-70">When there are new updates, they'll appear here.</p>
      </div>
    );
  }

  return (
    <div className="relative mt-6">
      {/* Vertical timeline line */}
      <div className={`absolute left-5 top-6 bottom-6 w-px ${isDark ? 'bg-gray-800' : 'bg-gray-200'}`} />

      <div className="space-y-4">
        {announcements.map((notif, index) => {
          const iconKey = notif.icon || TYPE_ICON_MAP[notif.type] || 'Megaphone';
          const IconComponent = ICON_MAP[iconKey] || Megaphone;
          const isUnread = !notif.read;

          return (
            <div
              key={`${notif.source}-${notif.id}`}
              className="relative pl-12"
              style={{ animation: `fadeSlideUp 0.4s ease-out ${index * 0.06}s both` }}
            >
              {/* Timeline dot */}
              <div
                className={`absolute left-[12px] top-5 w-[18px] h-[18px] rounded-full flex items-center justify-center z-10 ring-4 ${
                  isUnread
                    ? isDark ? 'bg-sky-500 ring-[#0a0f1f]' : 'bg-sky-500 ring-white'
                    : isDark ? 'bg-gray-700 ring-[#0a0f1f]' : 'bg-gray-300 ring-white'
                }`}
              >
                <IconComponent size={10} className="text-white" />
              </div>

              {/* Card */}
              <button
                onClick={() => handleCardClick(notif)}
                className={`w-full text-left rounded-xl p-5 border transition-all duration-200 ${
                  isUnread
                    ? isDark
                      ? 'bg-sky-500/5 border-sky-500/30 border-l-2 border-l-sky-500 hover:bg-sky-500/10'
                      : 'bg-sky-50 border-sky-200 border-l-2 border-l-sky-500 hover:bg-sky-100'
                    : isDark
                    ? 'bg-white/[0.02] border-gray-800 hover:bg-white/5'
                    : 'bg-white border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-sm leading-tight ${isUnread ? 'font-semibold' : 'font-medium opacity-80'}`}>
                      {notif.title}
                      {isUnread && (
                        <span className="ml-2 inline-block w-2 h-2 rounded-full bg-sky-500 align-middle" />
                      )}
                    </h3>

                    {notif.body && (
                      <p className={`text-sm mt-2 leading-relaxed whitespace-pre-wrap ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {notif.body}
                      </p>
                    )}

                    <div className="flex items-center gap-3 mt-3">
                      <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {notif.createdBy || 'System Admin'} &middot; {formatDate(notif.createdAt)}
                      </span>
                      {notif.link && (
                        <a
                          href={notif.link}
                          onClick={(e) => e.stopPropagation()}
                          className={`inline-flex items-center gap-1 text-xs font-medium ${
                            isDark ? 'text-sky-400 hover:text-sky-300' : 'text-sky-600 hover:text-sky-500'
                          }`}
                        >
                          <ExternalLink size={12} />
                          View
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// SETTINGS TAB
// ═══════════════════════════════════════════════════════════════
function SettingsTab({ isDark }) {
  const { currentUser, isAdmin, isStaff } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [preferences, setPreferences] = useState({});
  const [quietHours, setQuietHours] = useState({ enabled: false, start: '22:00', end: '08:00' });
  const [emailPreferences, setEmailPreferences] = useState({
    emailCourseUpdates: true,
    emailWeeklyProgress: true,
    emailPromotions: true,
    emailCertificates: true,
  });
  const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);

  const userRole = isAdmin ? 'admin' : isStaff ? 'specialist' : 'learner';

  useEffect(() => {
    loadPreferences();
    checkPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  const checkPermission = () => {
    if (isNotificationSupported()) {
      setPermissionStatus(getNotificationPermission());
    } else {
      setPermissionStatus('unsupported');
    }
  };

  const loadPreferences = async () => {
    if (!currentUser?.uid) { setLoading(false); return; }
    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setPreferences(data.notificationPreferences || DEFAULT_PREFERENCES[userRole] || {});
        setQuietHours(data.quietHours || { enabled: false, start: '22:00', end: '08:00' });
        setEmailPreferences(data.emailPreferences || {
          emailCourseUpdates: true,
          emailWeeklyProgress: true,
          emailPromotions: true,
          emailCertificates: true,
        });
        if (data.timezone) setTimezone(data.timezone);
      } else {
        setPreferences(DEFAULT_PREFERENCES[userRole] || {});
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      setPreferences(DEFAULT_PREFERENCES[userRole] || {});
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    if (!currentUser?.uid) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'users', currentUser.uid), {
        notificationPreferences: preferences,
        quietHours,
        emailPreferences,
        timezone,
        notificationPreferencesUpdatedAt: serverTimestamp()
      }, { merge: true });
      toast.success('Notification preferences saved');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const enableNotifications = async () => {
    const permission = await requestNotificationPermission();
    setPermissionStatus(permission);
    if (permission === 'granted') {
      await getFCMTokenAndSave(currentUser.uid, userRole);
      toast.success('Notifications enabled!');
    } else if (permission === 'denied') {
      toast.error('Notification permission denied. Please enable in browser settings.');
    }
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleEmailPreference = (key) => {
    setEmailPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const EmailToggle = ({ id, icon: Icon, label, description }) => (
    <label
      htmlFor={`email-${id}`}
      className={`flex items-center justify-between p-4 rounded-xl border transition-colors cursor-pointer group ${
        isDark ? 'border-gray-700 hover:border-blue-500/50' : 'border-gray-200 hover:border-blue-500/50'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500 group-hover:bg-blue-500/20 transition-colors">
          <Icon size={20} />
        </div>
        <div>
          <p className="font-semibold text-sm">{label}</p>
          <p className="text-xs opacity-50">{description}</p>
        </div>
      </div>
      <div className="relative">
        <input
          type="checkbox"
          id={`email-${id}`}
          checked={emailPreferences[id] || false}
          onChange={() => toggleEmailPreference(id)}
          className="sr-only"
        />
        <div className={`w-12 h-6 rounded-full transition-colors ${
          emailPreferences[id] ? 'bg-blue-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'
        }`}>
          <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${
            emailPreferences[id] ? 'translate-x-6' : 'translate-x-0.5'
          } mt-0.5`} />
        </div>
      </div>
    </label>
  );

  const PreferenceToggle = ({ id, icon: Icon, label, description }) => (
    <label
      htmlFor={id}
      className={`flex items-center justify-between p-4 rounded-xl border transition-colors cursor-pointer group ${
        isDark ? 'border-gray-700 hover:border-blue-500/50' : 'border-gray-200 hover:border-blue-500/50'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500 group-hover:bg-blue-500/20 transition-colors">
          <Icon size={20} />
        </div>
        <div>
          <p className="font-semibold text-sm">{label}</p>
          <p className="text-xs opacity-50">{description}</p>
        </div>
      </div>
      <div className="relative">
        <input
          type="checkbox"
          id={id}
          checked={preferences[id] || false}
          onChange={() => togglePreference(id)}
          className="sr-only"
        />
        <div className={`w-12 h-6 rounded-full transition-colors ${
          preferences[id] ? 'bg-blue-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'
        }`}>
          <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${
            preferences[id] ? 'translate-x-6' : 'translate-x-0.5'
          } mt-0.5`} />
        </div>
      </div>
    </label>
  );

  if (loading) {
    return (
      <div className="mt-6 space-y-4">
        <div className={`h-24 rounded-xl animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-100'}`} />
        <div className={`h-16 rounded-xl animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-100'}`} />
        <div className={`h-16 rounded-xl animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-100'}`} />
      </div>
    );
  }

  return (
    <div className="space-y-8 mt-6">
      {/* Permission Status Card */}
      <div className={`p-5 rounded-2xl border ${
        permissionStatus === 'granted'
          ? 'bg-green-500/10 border-green-500/30'
          : permissionStatus === 'denied'
          ? 'bg-red-500/10 border-red-500/30'
          : 'bg-yellow-500/10 border-yellow-500/30'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {permissionStatus === 'granted' ? (
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <Bell size={24} className="text-green-500" />
              </div>
            ) : permissionStatus === 'denied' ? (
              <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
                <BellOff size={24} className="text-red-500" />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <Bell size={24} className="text-yellow-500" />
              </div>
            )}
            <div>
              <p className="font-bold">
                {permissionStatus === 'granted' && 'Notifications Enabled'}
                {permissionStatus === 'denied' && 'Notifications Blocked'}
                {permissionStatus === 'default' && 'Enable Notifications'}
                {permissionStatus === 'unsupported' && 'Notifications Not Supported'}
              </p>
              <p className="text-xs opacity-60">
                {permissionStatus === 'granted' && 'You will receive push notifications'}
                {permissionStatus === 'denied' && 'Enable in browser settings to receive notifications'}
                {permissionStatus === 'default' && 'Allow notifications to stay updated'}
                {permissionStatus === 'unsupported' && 'Your browser does not support push notifications'}
              </p>
            </div>
          </div>
          {permissionStatus === 'default' && (
            <button
              onClick={enableNotifications}
              className="px-4 py-2 rounded-xl bg-blue-500 text-white text-sm font-bold hover:bg-blue-600 transition-colors"
            >
              Enable
            </button>
          )}
        </div>
      </div>

      {/* Learner Preferences */}
      {userRole === 'learner' && (
        <div className="space-y-4">
          <h2 className="text-sm font-black uppercase tracking-widest opacity-40">Learning Notifications</h2>
          <div className="space-y-3">
            <PreferenceToggle id="newCourses" icon={BookOpen} label="New Courses Available" description="When new courses are added to your roadmap" />
            <PreferenceToggle id="courseMilestones" icon={Trophy} label="Course Milestones" description="Progress updates at 25%, 50%, 75%, 100%" />
            <PreferenceToggle id="dailyReminders" icon={Clock} label="Daily Learning Reminders" description="Gentle nudges to keep learning" />
            <PreferenceToggle id="chatMessages" icon={MessageCircle} label="Chat Messages" description="When support responds to your messages" />
            <PreferenceToggle id="newTools" icon={Smartphone} label="New Learning Tools" description="When new tools or features are available" />
            <PreferenceToggle id="subscriptionRenewal" icon={CreditCard} label="Subscription Renewal" description="Reminders before your subscription renews" />
            <PreferenceToggle id="certificateReady" icon={Trophy} label="Certificate Ready" description="When your completion certificate is available" />
            <PreferenceToggle id="weeklyProgress" icon={Mail} label="Weekly Progress Report" description="Summary of your weekly learning activity" />
          </div>
        </div>
      )}

      {/* Admin Preferences */}
      {userRole === 'admin' && (
        <div className="space-y-4">
          <h2 className="text-sm font-black uppercase tracking-widest opacity-40">Admin Notifications</h2>
          <div className="space-y-3">
            <PreferenceToggle id="newSignups" icon={Users} label="New User Signups" description="When a new user creates an account" />
            <PreferenceToggle id="chatMessages" icon={MessageCircle} label="New Chat Messages" description="When visitors start or send chat messages" />
            <PreferenceToggle id="agentClockInOut" icon={Clock} label="Agent Clock In/Out" description="When specialists clock in or out" />
            <PreferenceToggle id="payments" icon={CreditCard} label="Payment Received" description="When a payment is processed" />
            <PreferenceToggle id="supportTickets" icon={AlertCircle} label="Support Tickets" description="When new support tickets are created" />
          </div>
        </div>
      )}

      {/* Specialist Preferences */}
      {userRole === 'specialist' && (
        <div className="space-y-4">
          <h2 className="text-sm font-black uppercase tracking-widest opacity-40">Support Notifications</h2>
          <div className="space-y-3">
            <PreferenceToggle id="assignedChats" icon={MessageCircle} label="Assigned Chats" description="When a chat is assigned to you" />
            <PreferenceToggle id="highPriorityMessages" icon={AlertCircle} label="High Priority Messages" description="Urgent messages from premium members" />
          </div>
        </div>
      )}

      {/* Quiet Hours */}
      <div className="space-y-4">
        <h2 className="text-sm font-black uppercase tracking-widest opacity-40">Quiet Hours</h2>
        <div className={`p-4 rounded-xl border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Moon size={20} className="text-indigo-500" />
              <div>
                <p className="font-semibold text-sm">Enable Quiet Hours</p>
                <p className="text-xs opacity-50">No notifications during specified times</p>
              </div>
            </div>
            <button
              onClick={() => setQuietHours(prev => ({ ...prev, enabled: !prev.enabled }))}
              className={`w-12 h-6 rounded-full transition-colors ${
                quietHours.enabled ? 'bg-indigo-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'
              }`}
            >
              <div className={`w-5 h-5 rounded-full bg-white shadow-md transform transition-transform ${
                quietHours.enabled ? 'translate-x-6' : 'translate-x-0.5'
              } mt-0.5`} />
            </button>
          </div>
          {quietHours.enabled && (
            <div className={`flex items-center gap-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex-1">
                <label className="text-xs font-medium opacity-60 block mb-1">Start</label>
                <input
                  type="time"
                  value={quietHours.start}
                  onChange={(e) => setQuietHours(prev => ({ ...prev, start: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border bg-transparent text-sm ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
                />
              </div>
              <div className="flex-1">
                <label className="text-xs font-medium opacity-60 block mb-1">End</label>
                <input
                  type="time"
                  value={quietHours.end}
                  onChange={(e) => setQuietHours(prev => ({ ...prev, end: e.target.value }))}
                  className={`w-full px-3 py-2 rounded-lg border bg-transparent text-sm ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
                />
              </div>
            </div>
          )}
        </div>

        {/* Timezone */}
        <div className={`p-4 rounded-xl border mt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3 mb-3">
            <Globe size={20} className="text-indigo-500" />
            <div>
              <p className="font-semibold text-sm">Timezone</p>
              <p className="text-xs opacity-50">Used for quiet hours and scheduled notifications</p>
            </div>
          </div>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border bg-transparent text-sm ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
          >
            {Intl.supportedValuesOf?.('timeZone')?.map((tz) => (
              <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
            )) || (
              <option value={timezone}>{timezone.replace(/_/g, ' ')}</option>
            )}
          </select>
          <p className="text-xs opacity-40 mt-1">Auto-detected: {Intl.DateTimeFormat().resolvedOptions().timeZone.replace(/_/g, ' ')}</p>
        </div>
      </div>

      {/* Email Notifications */}
      <div className="space-y-4">
        <h2 className="text-sm font-black uppercase tracking-widest opacity-40">Email Notifications</h2>
        <div className="space-y-3">
          <EmailToggle id="emailCourseUpdates" icon={BookOpen} label="Course Updates" description="Email updates about new and updated courses" />
          <EmailToggle id="emailWeeklyProgress" icon={TrendingUp} label="Weekly Progress" description="Weekly summary of your learning progress" />
          <EmailToggle id="emailPromotions" icon={Megaphone} label="Promotions & Offers" description="Special offers and promotional emails" />
          <EmailToggle id="emailCertificates" icon={Award} label="Certificate Notifications" description="Email when your certificates are ready" />
        </div>
      </div>

      {/* Save Button */}
      <button
        onClick={savePreferences}
        disabled={saving}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold hover:from-blue-500 hover:to-indigo-500 transition-all disabled:opacity-50"
      >
        {saving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Save size={18} />
            Save Preferences
          </>
        )}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN EXPORT — Tabbed page
// ═══════════════════════════════════════════════════════════════
const TABS = [
  { key: 'announcements', label: 'Announcements', icon: Megaphone },
  { key: 'settings', label: 'Settings', icon: Settings },
];

export default function NotificationSettings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isDark = (localStorage.getItem('scope180-theme') || 'light') === 'dark';

  const activeTab = searchParams.get('tab') || 'announcements';

  const setTab = (key) => {
    setSearchParams({ tab: key }, { replace: true });
  };

  return (
    <div className={`max-w-3xl mx-auto px-2 py-4 md:p-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
      {/* Tab Bar */}
      <div className={`flex border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-3 md:px-5 py-3 text-sm font-semibold border-b-2 transition-colors -mb-px ${
              activeTab === key
                ? isDark
                  ? 'border-sky-500 text-sky-400'
                  : 'border-sky-500 text-sky-600'
                : isDark
                ? 'border-transparent text-gray-500 hover:text-gray-300'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            <Icon size={16} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'announcements' ? (
        <AnnouncementsTab isDark={isDark} />
      ) : (
        <SettingsTab isDark={isDark} />
      )}
    </div>
  );
}
